import type { Language } from '@eformulieren/engine'
import { type ReactNode, useId } from 'react'
import { inLanguage } from '../language/text.js'
import { sanitizeCitationHtml } from './sanitizeHtml.js'
import { StyleCredit } from './StyleCredit.js'
import type { CitationResult } from './types.js'

/**
 * De literatuurlijst aan het eind van een laag, als de inrichter er geen plaatste (opdracht van
 * 30 oktober 2026, R-46, R-50, eisenboek 12.9).
 *
 * In CitatenPOC staat de literatuurlijst altijd onder het document (`Bibliography.tsx`). Hier is de
 * referentielijst een vast component dat de inrichter plaatst (R-46, T4), en dat blijft de plek als
 * het er staat. Heeft een laag citaties maar staat het component nergens in die laag, dan tekent de
 * schil deze lijst na de laatste sectie van de laag: dezelfde uitkomst als de labels in de tekst
 * (`useSectionCitations`), dus dezelfde nummering, en alleen te lezen. Een eigen landmark met een
 * kop, zodat een schermlezer erheen kan springen.
 *
 * De regels zijn HTML van citeproc uit brondata die de invuller intypt; ze worden gesaniteerd zoals
 * in `ReferenceList.tsx`.
 */

const HEADER: Record<'invoer' | 'toets', { nl: string; en: string }> = {
  invoer: { nl: 'Literatuurlijst', en: 'References' },
  toets: { nl: 'Literatuurlijst bij de toets', en: 'References of the review' },
}

export interface LayerReferenceListProps {
  readonly laag: 'invoer' | 'toets'
  readonly taal: Language
  readonly literatuurlijst: CitationResult['literatuurlijst']
}

export function LayerReferenceList({ laag: layer, taal: language, literatuurlijst: entries }: LayerReferenceListProps): ReactNode {
  const headerId = useId()
  return (
    <section className="ef-laaglijst" aria-labelledby={headerId} data-laag={layer}>
      <h2 className="ef-laaglijst__kop" id={headerId}>
        {inLanguage(HEADER[layer], language)}
      </h2>
      <ul className="ef-referentielijst__lijst">
        {entries.map((rule) => (
          <li
            key={rule.bronId}
            className="ef-referentielijst__regel"
            // eslint-disable-next-line react/no-danger -- gesaniteerd, zie de kop hierboven
            dangerouslySetInnerHTML={{ __html: sanitizeCitationHtml(rule.html) }}
          />
        ))}
      </ul>
      <StyleCredit taal={language} />
    </section>
  )
}
